import React, { memo, useCallback, useLayoutEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import styled from "styled-components";

import {
  ModalStyled,
  ModalBackgroundStyled,
} from "../../common/ModalComponent/styles";
import {
  MissionPlannerContainerStyled,
  MissionPlannerContentStyled,
} from "../../MissionDetails/styles";

const RightAlignedContainerStyled = styled(MissionPlannerContainerStyled)`
  left: auto;
  right: 0;
`;

import { closeAnyModal, getModalData } from "Slices/modals";
import { fetchDeployMissionPlan } from "Slices/missionPlans";
import { getStopsByCurrentSemanticMap } from "Slices/stops";
import { getCurrentFleetId } from "Slices/missionPlanner";
import { DeployedMissionList } from "../../DeployedMissionList";
import { getCurrentSemanticMapId } from "Slices/appState";
import { getVdaActionDefinitions } from "Slices/vda_action_definitions";
import { StationMissionList } from "Components/StationView/components/StationMissionList/StationMissionList";

export const DeployedMissionListModal = memo(() => {
  const [editData, setEditData] = useState(null);
  const dispatch = useDispatch();
  const location = useLocation();
  const stops = useSelector(getStopsByCurrentSemanticMap);
  const currentFleetId = useSelector(getCurrentFleetId);
  const vdaActionDefinitions = useSelector(getVdaActionDefinitions);
  const semanticMapId = useSelector(getCurrentSemanticMapId);
  const modalData = useSelector(getModalData);
  const contextVehicleId: string | null = modalData?.vehicleId
    ? String(modalData.vehicleId)
    : null;
  const contextStationId: string | undefined = modalData?.stationId
    ? String(modalData.stationId)
    : undefined;
  // Vehicle-scoped open (StationView Dispatch) → right-side panel for that
  // vehicle. Plain MissionPlanner flow opens without a vehicleId → left-aligned
  // full planner. stationId is just a hint for which list to render.
  const isVehicleContext = Boolean(contextVehicleId);
  const isStationContext = Boolean(contextStationId);
  const Container = isVehicleContext
    ? RightAlignedContainerStyled
    : MissionPlannerContainerStyled;

  // Fleet ID is only required for the generic fleet-planner flow; station and
  // vehicle contexts both carry enough scope on their own.
  useLayoutEffect(() => {
    if (isStationContext || isVehicleContext) return;

    const searchParams = new URLSearchParams(location.search);
    const fleetIdFromUrl = searchParams.get("fleetId");

    if (currentFleetId || fleetIdFromUrl) return;

    toast.error("Please specify particular fleet before create new mission!");
    handleCloseModal();
  }, [currentFleetId, location.search, isStationContext, isVehicleContext]);

  const handleDeployPlanMissionAction = useCallback(
    (data: { id: string; vehicle?: string; [key: string]: any }) => {
      if (isStationContext) {
        dispatch(
          fetchDeployMissionPlan({
            ...data,
            stationId: contextStationId,
          })
        );
        return;
      }

      // Vehicle-scoped open (StationView Dispatch) without a stationId hint:
      // dispatch with the vehicle so the deploy lands on the right vehicle
      // instead of silently failing the fleet-id guard below.
      if (isVehicleContext && contextVehicleId) {
        dispatch(
          fetchDeployMissionPlan({
            ...data,
            vehicle: contextVehicleId,
          })
        );
        return;
      }

      if (!currentFleetId) return;
      dispatch(fetchDeployMissionPlan({ ...data, currentFleetId }));
    },
    [
      currentFleetId,
      isStationContext,
      contextStationId,
      isVehicleContext,
      contextVehicleId,
      dispatch,
    ]
  );

  const handleCloseModal = useCallback(() => {
    dispatch(closeAnyModal());
  }, [dispatch]);

  return (
    <ModalStyled>
      <ModalBackgroundStyled onClick={handleCloseModal} />
      <Container>
        <MissionPlannerContentStyled>
          {isVehicleContext && contextVehicleId ? (
            <StationMissionList
              vehicleId={contextVehicleId}
              onClose={handleCloseModal}
              deployPlanMissionAction={handleDeployPlanMissionAction}
            />
          ) : (
            <DeployedMissionList
              vehicleName={null}
              editData={editData}
              setEditData={setEditData}
              stops={stops}
              vehicleId={contextVehicleId}
              semanticMapId={semanticMapId}
              activeTab={"savedMission"}
              setActiveTab={null}
              deployPlanMissionAction={handleDeployPlanMissionAction}
              cancelAction={handleCloseModal}
              previousMode="Continue"
              vdaActionDefinitions={vdaActionDefinitions}
            />
          )}
        </MissionPlannerContentStyled>
      </Container>
    </ModalStyled>
  );
});
